import Phaser from "phaser";
import { boxInTileRange, type AssetManifest, type TileRange, type TileRangeOut } from "daedalus-core";
import { framesByPage, pageTextureKey, type PageFrame } from "./atlas.ts";
import { animatedCellDepth, rowSortFraction, type ChunkPlacement, type RowSort } from "./chunk-placement.ts";
import { collectStamps, createStampBuffer, stampConfig, type ChunkCells, type Stamp, type StampBuffer } from "./chunk-stamps.ts";
import type { TileAnimator } from "./tile-animator.ts";
import type { ChunkTexturePool, PooledChunkTexture } from "./chunk-texture-pool.ts";

/**
 * Chunk textures (ADR-019 item 5): one `DynamicTexture` per chunk per tile layer, painted once by stamping
 * frames, shown by one `Image`. Phaser 4 buffers dynamic texture drawing until `render()` is called, so a
 * paint ends with one render. Textures exist only for chunks the caller keeps alive, so memory stays bounded
 * by the camera view rather than the map, and a chunk of a layer with nothing in it costs nothing at all:
 * the stamps are collected before anything is allocated, and an empty chunk keeps no texture, image or row
 * strip, so a world of many layers pays only for the chunks that hold tiles. Cells whose frame animates
 * (ADR-041) also get one `Image` each at `animatedCellDepth`, driven by the shared `TileAnimator`, and go
 * away with the chunk: a hair above a flat chunk in the Players band, so it stays under every character as
 * the chunk does, and half a depth above a ground chunk. Only the cells near the camera are driven
 * (`cullAnimated`, P1.6-T6): a cell outside the tile range it is given holds no image and follows nothing,
 * and the frame baked into the chunk texture shows there, so the animator serves what can be seen rather than
 * every cell of the chunk range. A placement with `rowSort` (ADR-040, ADR-047: a tile
 * layer in the Players band) is shown as one cropped `Image` at `depth + sortFraction(row)` for each row a
 * stamp draws into, so its tiles interleave with props and characters by row; an animated cell in such a row
 * sits a hair above its strip, under the band's per-layer step.
 *
 * A view made with a pool (ADR-019's amendment of 2026-10-03) keeps the texture and image of a released chunk,
 * hidden, up to that many, and paints the next new chunk into one of them: creating a texture allocates it and its
 * framebuffer on the GPU and waits for the framebuffer's status, which is most of what painting a small chunk costs.
 * A texture's key is therefore a serial number, not its placement, and one no other view in the game holds. The
 * textures live in the game's texture manager, which a scene's shutdown does not empty: the owner calls `clear` then.
 *
 * A `DynamicTexture` loses its contents when the browser loses the WebGL context, and Phaser draws nothing back when
 * the context returns (P1.6-T236). While the renderer's context is lost `paint` draws nothing and remembers the
 * placement; when the renderer says `restorewebgl` the view tells its owner, one call each, every placement that is
 * live and every one that was refused meanwhile, and the owner paints it again from its own cells (the view keeps
 * none: the editor reuses one buffer for every chunk). The view listens from its first paint to its next `clear`.
 */

/**
 * The renderer's event for a WebGL context that came back (`Phaser.Renderer.Events.RESTORE_WEBGL`), named here so the
 * module reads it off the scene without a second import of Phaser's event table.
 */
const RESTORE_WEBGL = "restorewebgl";

/**
 * What the view reads off a scene's renderer: whether its WebGL context is lost now, and its events. A canvas
 * renderer has no `contextLost`, which reads as never lost.
 */
interface ContextHost {
  readonly contextLost?: boolean;
  on(event: string, listener: () => void): unknown;
  off(event: string, listener: () => void): unknown;
}

/**
 * The renderer of a scene, as far as the view needs it.
 *
 * @param scene The scene.
 * @returns The renderer, or null when the scene has none to listen to.
 */
function contextHostOf(scene: Phaser.Scene): ContextHost | null {
  const renderer = (scene as unknown as { readonly sys?: { readonly renderer?: ContextHost } }).sys?.renderer;
  return renderer !== undefined && typeof renderer.on === "function" ? renderer : null;
}

/**
 * One animated cell (ADR-041) of a live chunk: where it sits in world pixels and in tiles, the frames it covers, and
 * its image while the view's tile range holds it (`image` is null for a cell outside it, which follows nothing).
 */
interface AnimatedCell {
  readonly frameId: string;
  readonly texture: string;
  readonly x: number;
  readonly y: number;
  readonly depth: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly tx: number;
  readonly ty: number;
  readonly spanX: number;
  readonly spanY: number;
  image: Phaser.GameObjects.Image | null;
}

/**
 * A live chunk texture, its image, the cropped image of each row that holds tiles when the placement
 * row-sorts, and its animated cells (ADR-041). `visible` is what the caller last asked for, which images made
 * by a later paint inherit.
 */
interface LiveChunk {
  placement: ChunkPlacement;
  readonly texture: Phaser.Textures.DynamicTexture;
  readonly image: Phaser.GameObjects.Image;
  readonly rows: { readonly row: number; readonly image: Phaser.GameObjects.Image }[];
  readonly animated: AnimatedCell[];
  visible: boolean;
}

/**
 * Registers every atlas page of a manifest as a Phaser texture with the manifest's frames. Each page's loaded
 * image texture is renamed to the page's key and given its frames in place, so each page is uploaded to the
 * GPU once and no intermediate atlas JSON is built or cloned per frame.
 *
 * @param scene Scene whose loader already loaded each page image under `pageTextureKey(manifest, i) + ":image"`.
 * @param manifest Asset manifest.
 * @param onPage Told after each page is registered, with how many are done and how many there are.
 * @throws Error when a page image was not loaded, or a frame names a page the manifest lacks.
 */
export function registerAtlasPages(scene: Phaser.Scene, manifest: AssetManifest, onPage: (done: number, total: number) => void = () => undefined): void {
  const pages = framesByPage(manifest);
  pages.forEach((frames, page) => {
    registerAtlasPage(scene, manifest, page, frames);
    onPage(page + 1, pages.length);
  });
}

/**
 * Registers one atlas page: renames its loaded image texture to the page's key and adds its frames. A page
 * already registered is left as it is.
 *
 * @param scene Scene whose loader loaded the page image under `pageImageKey(manifest, page)`.
 * @param manifest Asset manifest.
 * @param page Page index.
 * @param frames The page's frames (`framesByPage`).
 * @throws Error when the page image was not loaded.
 */
export function registerAtlasPage(scene: Phaser.Scene, manifest: AssetManifest, page: number, frames: readonly PageFrame[]): void {
  const key = pageTextureKey(manifest, page);
  if (scene.textures.exists(key)) {
    return;
  }
  const imageKey = pageImageKey(manifest, page);
  if (!scene.textures.exists(imageKey)) {
    throw new Error(`page image ${imageKey} has not been loaded`);
  }
  scene.textures.renameTexture(imageKey, key);
  const texture = scene.textures.get(key);
  for (const frame of frames) {
    texture.add(frame.id, 0, frame.x, frame.y, frame.w, frame.h);
  }
}

/**
 * The key a page image must be loaded under before `registerAtlasPages`.
 *
 * @param manifest Asset manifest.
 * @param page Page index.
 * @returns The loader key.
 */
export function pageImageKey(manifest: AssetManifest, page: number): string {
  return `${pageTextureKey(manifest, page)}:image`;
}

/**
 * Owns the chunk textures of one map.
 */
export class ChunkView {
  private readonly live = new Map<string, LiveChunk>();
  private readonly spare: { readonly texture: Phaser.Textures.DynamicTexture; readonly image: Phaser.GameObjects.Image }[] = [];
  private readonly buffer: StampBuffer;
  private readonly stripped: Uint8Array;
  private made = 0;
  private held = 0;
  private driven = 0;
  private culled: TileRangeOut | null = null;
  private readonly refused = new Map<string, ChunkPlacement>();
  private listening: ContextHost | null = null;

  /**
   * Tells the owner every placement a restored context left without its pixels. A throw from one placement is
   * logged and does not stop the rest, since this runs inside the renderer's own restore.
   */
  private readonly onRestore = (): void => {
    const owner = this.restored;
    const told = new Map<string, ChunkPlacement>(this.refused);
    this.refused.clear();
    for (const [key, chunk] of this.live) {
      told.set(key, chunk.placement);
    }
    if (owner === null) {
      return;
    }
    for (const placement of told.values()) {
      try {
        owner(placement);
      } catch (error) {
        console.error(`chunk ${ChunkView.key(placement)} could not be painted again: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  };

  /**
   * @param scene Scene to draw into.
   * @param tileSize Tile size in pixels.
   * @param chunkSize Chunk size in tiles.
   * @param animator Animator for cells whose frame animates (ADR-041); null bakes frame 0 only.
   * @param pool The most released chunk textures the view keeps, hidden, for the chunks painted next; 0 destroys
   *   every released texture. Each kept texture holds `(chunkSize * tileSize)^2 * 4` bytes of GPU memory.
   * @param restored Told each placement to paint again when a lost WebGL context comes back; null to be told none.
   * @param shared Textures shared with the scene's other chunk views (`ChunkTexturePool`): a paint with no spare of the
   *   view's own takes one from it before making a texture, and `clear` gives it every texture the view holds; null
   *   shares none.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly tileSize: number,
    private readonly chunkSize: number,
    private readonly animator: TileAnimator | null = null,
    private readonly pool = 0,
    private readonly restored: ((placement: ChunkPlacement) => void) | null = null,
    private readonly shared: ChunkTexturePool | null = null,
  ) {
    this.buffer = createStampBuffer(chunkSize);
    this.stripped = new Uint8Array(chunkSize);
  }

  /**
   * Number of chunk textures currently alive.
   *
   * @returns The count.
   */
  public get textureCount(): number {
    return this.live.size;
  }

  /**
   * Number of released chunk textures kept for the chunks painted next.
   *
   * @returns The count.
   */
  public get pooled(): number {
    return this.spare.length;
  }

  /**
   * Number of animated cells in the live chunks, driven or not.
   *
   * @returns The count.
   */
  public get animatedHeld(): number {
    return this.held;
  }

  /**
   * Number of animated cells in the live chunks that hold an image and follow their animation.
   *
   * @returns The count.
   */
  public get animatedDriven(): number {
    return this.driven;
  }

  /**
   * Drives only the animated cells that reach into a tile range, and keeps every cell painted later to the same
   * range until it is changed. A cell leaving the range loses its image and stops following its animation; a cell
   * entering it gets an image at once, showing the frame the shared clock has reached. A range equal to the last
   * costs one comparison. The game's painter never calls this, and so drives every cell.
   *
   * @param range The tiles to drive, or null to drive every cell.
   */
  public cullAnimated(range: TileRange | null): void {
    const current = this.culled;
    if (range === null) {
      if (current === null) {
        return;
      }
      this.culled = null;
    } else if (current !== null && current.minTx === range.minTx && current.minTy === range.minTy && current.maxTx === range.maxTx && current.maxTy === range.maxTy) {
      return;
    } else if (current === null) {
      this.culled = { minTx: range.minTx, minTy: range.minTy, maxTx: range.maxTx, maxTy: range.maxTy };
    } else {
      current.minTx = range.minTx;
      current.minTy = range.minTy;
      current.maxTx = range.maxTx;
      current.maxTy = range.maxTy;
    }
    for (const chunk of this.live.values()) {
      for (const cell of chunk.animated) {
        this.driveCell(chunk, cell);
      }
    }
  }

  /**
   * Gives a cell an image and a place in the animator while the cull range holds it, and takes both away when it does
   * not.
   *
   * @param chunk The cell's chunk.
   * @param cell The cell.
   */
  private driveCell(chunk: LiveChunk, cell: AnimatedCell): void {
    const animator = this.animator;
    if (animator === null) {
      return;
    }
    const range = this.culled;
    const wanted = range === null || boxInTileRange(range, cell.tx, cell.ty, cell.spanX, cell.spanY);
    if (wanted && cell.image === null) {
      const image = this.scene.add.image(cell.x, cell.y, cell.texture, cell.frameId).setOrigin(0, 0).setFlip(cell.flipX, cell.flipY).setDepth(cell.depth).setVisible(chunk.visible);
      animator.follow(cell.frameId, image);
      cell.image = image;
      this.driven += 1;
    } else if (!wanted && cell.image !== null) {
      animator.unfollow(cell.frameId, cell.image);
      cell.image.destroy();
      cell.image = null;
      this.driven -= 1;
    }
  }

  /**
   * Paints a chunk of a layer, replacing any earlier texture at the same placement. The chunk's stamps are
   * collected first, so a chunk of a layer that holds nothing there allocates nothing: any placement that
   * exists is released and `has` reports false, leaving a later paint to allocate normally when the caller
   * paints into the chunk.
   *
   * @param placement Layer and chunk position.
   * @param cells The chunk's packed cells and lookup tables.
   * @returns Number of stamps drawn; none while the renderer's WebGL context is lost, when the placement is kept for
   *   the owner to paint again once it is back.
   * @throws Error when a cell's frame is missing from the manifest; the placement is then released and not
   *   painted at all, so a later call retries it.
   */
  public paint(placement: ChunkPlacement, cells: ChunkCells): number {
    this.listen();
    if (contextHostOf(this.scene)?.contextLost === true) {
      this.refused.set(ChunkView.key(placement), placement);
      return 0;
    }
    try {
      collectStamps(cells, this.buffer);
    } catch (error) {
      this.release(placement);
      throw error;
    }
    const stamps = this.buffer.count;
    if (stamps === 0) {
      this.release(placement);
      return 0;
    }
    const key = ChunkView.key(placement);
    const existing = this.live.get(key);
    const pixels = this.chunkSize * this.tileSize;
    let chunk: LiveChunk;
    if (existing === undefined) {
      const kept = this.spare.pop() ?? this.shared?.take(pixels) ?? undefined;
      if (kept === undefined) {
        const textureKey = this.freeKey();
        const texture = this.scene.textures.addDynamicTexture(textureKey, pixels, pixels);
        if (texture === null) {
          throw new Error(`could not create chunk texture ${textureKey} for ${key}`);
        }
        const image = this.scene.add.image(placement.cx * pixels, placement.cy * pixels, textureKey).setOrigin(0, 0).setDepth(placement.depth);
        chunk = { placement, texture, image, rows: [], animated: [], visible: true };
      } else {
        kept.texture.clear();
        kept.image.setPosition(placement.cx * pixels, placement.cy * pixels).setDepth(placement.depth).setAlpha(1);
        chunk = { placement, texture: kept.texture, image: kept.image, rows: [], animated: [], visible: true };
      }
      chunk.image.setVisible(placement.rowSort === undefined);
    } else {
      existing.texture.clear();
      this.releaseAnimated(existing);
      chunk = existing;
      chunk.placement = placement;
    }
    this.live.set(key, chunk);
    const texture = chunk.texture;
    const animator = this.animator;
    try {
      if (placement.rowSort !== undefined) {
        this.syncRows(chunk, placement, placement.rowSort);
      }
      for (let i = 0; i < stamps; i += 1) {
        const stamp = this.buffer.stamps[i] as Stamp;
        texture.stamp(stamp.texture, stamp.frame, stamp.x, stamp.y, stampConfig(stamp));
        if (animator !== null && animator.animates(stamp.frame)) {
          const rect = cells.manifest.frames[stamp.frame];
          const cell: AnimatedCell = {
            frameId: stamp.frame,
            texture: stamp.texture,
            x: placement.cx * pixels + stamp.x,
            y: placement.cy * pixels + stamp.y,
            depth: animatedCellDepth(placement, stamp.y, this.chunkSize, this.tileSize),
            flipX: stamp.flipX,
            flipY: stamp.flipY,
            tx: placement.cx * this.chunkSize + Math.floor(stamp.x / this.tileSize),
            ty: placement.cy * this.chunkSize + Math.floor(stamp.y / this.tileSize),
            spanX: rect === undefined ? 1 : Math.max(1, Math.ceil(rect.w / this.tileSize)),
            spanY: rect === undefined ? 1 : Math.max(1, Math.ceil(rect.h / this.tileSize)),
            image: null,
          };
          chunk.animated.push(cell);
          this.held += 1;
          this.driveCell(chunk, cell);
        }
      }
      texture.render();
    } catch (error) {
      this.release(placement);
      throw error;
    }
    return stamps;
  }

  /**
   * Starts listening to the scene's renderer for a restored context, once.
   */
  private listen(): void {
    if (this.listening !== null || this.restored === null) {
      return;
    }
    const host = contextHostOf(this.scene);
    if (host === null) {
      return;
    }
    host.on(RESTORE_WEBGL, this.onRestore);
    this.listening = host;
  }

  /**
   * Stops listening to the renderer.
   */
  private unlisten(): void {
    this.listening?.off(RESTORE_WEBGL, this.onRestore);
    this.listening = null;
  }

  /**
   * The next texture key no live texture holds. The texture manager is the game's, shared by every scene and so by
   * every view, and a view's textures outlive a scene that shuts down without `clear`, so a serial of the view's own
   * is tried against the manager until one is free.
   *
   * @returns `chunk:<n>`.
   */
  private freeKey(): string {
    let key = `chunk:${this.made}`;
    while (this.scene.textures.exists(key)) {
      this.made += 1;
      key = `chunk:${this.made}`;
    }
    this.made += 1;
    return key;
  }

  /**
   * Brings a row-sorted chunk's strips in line with the rows the collected stamps draw into: one cropped
   * image per such row at the row's sort depth (ADR-040 amendment, ADR-047), none for a row with nothing in
   * it. Strips of rows that still hold tiles are kept as they are, so a stroke over a chunk does not rebuild
   * what has not changed.
   *
   * @param chunk The chunk.
   * @param placement Layer and chunk position.
   * @param rowSort How the placement's rows sort.
   */
  private syncRows(chunk: LiveChunk, placement: ChunkPlacement, rowSort: RowSort): void {
    const rows = this.buffer.rows;
    const stripped = this.stripped;
    stripped.fill(0);
    let kept = 0;
    for (const strip of chunk.rows) {
      if (rows[strip.row] === 1) {
        stripped[strip.row] = 1;
        chunk.rows[kept] = strip;
        kept += 1;
      } else {
        strip.image.destroy();
      }
    }
    chunk.rows.length = kept;
    const pixels = this.chunkSize * this.tileSize;
    for (let row = 0; row < this.chunkSize; row += 1) {
      if (rows[row] !== 1 || stripped[row] === 1) {
        continue;
      }
      const image = this.scene.add.image(placement.cx * pixels, placement.cy * pixels, chunk.texture.key).setOrigin(0, 0).setDepth(placement.depth + rowSortFraction(rowSort, placement.cy * this.chunkSize + row)).setVisible(chunk.visible);
      image.setCrop(0, row * this.tileSize, pixels, this.tileSize);
      chunk.rows.push({ row, image });
    }
  }

  /**
   * Draws a live chunk at a given opacity, its row strips with it. A chunk that is not live is left alone.
   *
   * @param placement Layer and chunk position.
   * @param alpha Opacity from 0 to 1.
   */
  public setAlpha(placement: ChunkPlacement, alpha: number): void {
    const chunk = this.live.get(ChunkView.key(placement));
    if (chunk === undefined) {
      return;
    }
    chunk.image.setAlpha(alpha);
    for (const strip of chunk.rows) {
      strip.image.setAlpha(alpha);
    }
  }

  /**
   * Releases a chunk: its row strips and animated cells are destroyed, and its texture and image are kept hidden for
   * the next chunk painted while the pool has room, else destroyed.
   *
   * @param placement Layer and chunk position.
   */
  public release(placement: ChunkPlacement): void {
    const key = ChunkView.key(placement);
    this.refused.delete(key);
    const chunk = this.live.get(key);
    if (chunk === undefined) {
      return;
    }
    if (this.spare.length < this.pool) {
      chunk.image.setVisible(false);
      this.spare.push({ texture: chunk.texture, image: chunk.image });
    } else {
      chunk.image.destroy();
      chunk.texture.destroy();
    }
    this.releaseRows(chunk);
    this.releaseAnimated(chunk);
    this.live.delete(key);
  }

  /**
   * Destroys a chunk's row strips.
   *
   * @param chunk The chunk.
   */
  private releaseRows(chunk: LiveChunk): void {
    for (const strip of chunk.rows) {
      strip.image.destroy();
    }
    chunk.rows.length = 0;
  }

  /**
   * Forgets a chunk's animated cells, destroying their images and stopping them following their animations.
   *
   * @param chunk The chunk.
   */
  private releaseAnimated(chunk: LiveChunk): void {
    for (const cell of chunk.animated) {
      if (cell.image !== null) {
        this.animator?.unfollow(cell.frameId, cell.image);
        cell.image.destroy();
        this.driven -= 1;
      }
    }
    this.held -= chunk.animated.length;
    chunk.animated.length = 0;
  }

  /**
   * Shows or hides a painted chunk. A hidden layer keeps the textures it holds; a chunk of it that empties
   * is released like any other, and a later repaint of it is born visible.
   *
   * @param placement Layer and chunk position.
   * @param visible Whether shown.
   */
  public setVisible(placement: ChunkPlacement, visible: boolean): void {
    const chunk = this.live.get(ChunkView.key(placement));
    if (chunk === undefined) {
      return;
    }
    chunk.visible = visible;
    chunk.image.setVisible(visible && chunk.rows.length === 0);
    for (const strip of chunk.rows) {
      strip.image.setVisible(visible);
    }
    for (const cell of chunk.animated) {
      cell.image?.setVisible(visible);
    }
  }

  /**
   * Reports whether a chunk is painted.
   *
   * @param placement Layer and chunk position.
   * @returns True when alive.
   */
  public has(placement: ChunkPlacement): boolean {
    return this.live.has(ChunkView.key(placement));
  }

  /**
   * Releases every chunk and stops listening for a restored context; the next paint listens again. Each texture the
   * view held, painted or spare, goes to the shared pool while it has room and is destroyed otherwise.
   */
  public clear(): void {
    this.refused.clear();
    this.unlisten();
    const pixels = this.chunkSize * this.tileSize;
    for (const chunk of this.live.values()) {
      this.releaseRows(chunk);
      this.releaseAnimated(chunk);
      this.letGo(chunk, pixels);
    }
    this.live.clear();
    for (const kept of this.spare) {
      this.letGo(kept, pixels);
    }
    this.spare.length = 0;
  }

  /**
   * Gives a texture and its image to the shared pool, or destroys them when there is none or it is full.
   *
   * @param held The texture and its image.
   * @param pixels The texture's side in pixels.
   */
  private letGo(held: PooledChunkTexture, pixels: number): void {
    if (this.shared?.give(held, pixels) === true) {
      return;
    }
    held.image.destroy();
    held.texture.destroy();
  }

  /**
   * The map key of a placement.
   *
   * @param placement Layer and chunk position.
   * @returns `<layerId>:<cx>_<cy>`.
   */
  private static key(placement: ChunkPlacement): string {
    return `${placement.layerId}:${placement.cx}_${placement.cy}`;
  }
}
